import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlbumFrameRow,
  getAlbumFrames,
  updateAlbumFrameTags,
} from '../services/marketingApi';
import FrameModal, {
  ALBUM_URL,
  FAMILY_LABELS,
  FrameFamily,
  FrameThumb,
  LoadPill,
  familyOf,
  fmtDur,
  fmtMs,
} from '../components/Album/FrameModal';
import { Kpis, Empty, Loading, PageFrame, Pill, Section, Tabs, Th, btn, btnTone, input, td, trClick } from '../components/Kit';

type Family = 'all' | FrameFamily;
type Audio = 'all' | 'english' | 'telugu' | 'music' | 'silent';
type Sort = 'position' | 'views' | 'likes' | 'duration' | 'load' | 'dwell';

const audioKind = (a: string | null): Exclude<Audio, 'all'> | null => {
  if (!a) return null;
  const s = a.toLowerCase();
  if (s.includes('telugu')) return 'telugu';
  if (s.includes('voiceover') || s.includes('speech')) return 'english';
  if (s.includes('music')) return 'music';
  if (s.includes('none') || s.includes('silent')) return 'silent';
  return null;
};

const TagChip: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode; title?: string }> = ({ on, onClick, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-pressed={on}
    className={`rounded-full px-2.5 py-0.5 text-[12px] transition-colors ${
      on ? 'bg-forest text-warm-white font-semibold' : 'bg-charcoal/[0.06] text-charcoal hover:bg-charcoal/10'
    }`}
  >
    {children}
  </button>
);

const TOP_TAGS_SHOWN = 14;

const Album: React.FC = () => {
  const [frames, setFrames] = useState<AlbumFrameRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [family, setFamily] = useState<Family>('all');
  const [audio, setAudio] = useState<Audio>('all');
  const [sort, setSort] = useState<Sort>('position');
  const [selTags, setSelTags] = useState<string[]>([]);
  const [allTagsOpen, setAllTagsOpen] = useState(false);
  const [open, setOpen] = useState<AlbumFrameRow | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    getAlbumFrames()
      .then((r) => setFrames(r.frames))
      .finally(() => setLoading(false));
  }, []);

  const saveTags = async (f: AlbumFrameRow, tags: string[]) => {
    setFrames((prev) => prev.map((x) => (x.code === f.code ? { ...x, tags } : x)));
    setOpen((prev) => (prev && prev.code === f.code ? { ...prev, tags } : prev));
    await updateAlbumFrameTags(f.code, tags);
  };

  const copyLink = (code: string) => {
    navigator.clipboard.writeText(`${ALBUM_URL}?f=${code}`);
    setCopied(code);
    setTimeout(() => setCopied((c) => (c === code ? null : c)), 1500);
  };

  const tagCounts = useMemo(() => {
    const c = new Map<string, number>();
    frames.forEach((f) => f.tags.forEach((t) => c.set(t, (c.get(t) ?? 0) + 1)));
    return [...c.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [frames]);

  const rows = useMemo(() => {
    const filtered = frames.filter((f) =>
      (family === 'all' || familyOf(f.code) === family) &&
      (audio === 'all' || audioKind(f.audio) === audio) &&
      selTags.every((t) => f.tags.includes(t)),
    );
    const key: Record<Sort, (f: AlbumFrameRow) => number> = {
      position: (f) => f.position ?? Infinity,
      views: (f) => -f.stats.views,
      likes: (f) => -f.stats.likes,
      duration: (f) => -(f.duration_s ?? -1),
      load: (f) => -(f.stats.load_ms_p50 ?? -1), // slowest-loading first — find the laggards
      dwell: (f) => -(f.stats.dwell_ms_p50 ?? -1),
    };
    return filtered.sort((x, y) => key[sort](x) - key[sort](y) || (x.position ?? Infinity) - (y.position ?? Infinity));
  }, [frames, family, audio, selTags, sort]);

  const stats = useMemo(() => {
    const inFeed = frames.filter((f) => f.position != null).length;
    const sum = (k: keyof AlbumFrameRow['stats']) => frames.reduce((s, f) => s + (f.stats[k] ?? 0), 0);
    return [
      [`${inFeed} / ${frames.length}`, 'frames in feed'],
      [sum('views').toLocaleString('en-IN'), 'frame views'],
      [sum('likes').toLocaleString('en-IN'), 'likes'],
      [sum('shares').toLocaleString('en-IN'), 'shares'],
    ] as const;
  }, [frames]);

  return (
    <PageFrame
      title="Project Album"
      subline={<a href={ALBUM_URL} target="_blank" rel="noopener noreferrer" className="hover:text-forest">bhuvanafortunegreens.com/album ↗</a>}
      action={<Link to="/album/order" className={`${btn} ${btnTone.forest}`}>Edit feed order</Link>}
      bar={loading ? undefined : <>
        <Tabs<Family>
          value={family}
          onChange={setFamily}
          items={[['all', 'All'], ...(Object.keys(FAMILY_LABELS) as FrameFamily[]).map((k) => [k, FAMILY_LABELS[k]] as [Family, string])]}
        />
        <Tabs<Audio>
          value={audio}
          onChange={setAudio}
          items={[['all', 'Any audio'], ['english', 'English VO'], ['telugu', 'Telugu'], ['music', 'Music'], ['silent', 'Silent']]}
        />
        <select value={sort} onChange={(e) => setSort(e.target.value as Sort)} className={`${input} h-8`} aria-label="Sort frames">
          <option value="position">Feed order</option>
          <option value="views">Most viewed</option>
          <option value="likes">Most liked</option>
          <option value="duration">Longest first</option>
          <option value="load">Slowest load</option>
          <option value="dwell">Longest watched</option>
        </select>
      </>}
    >
      {loading ? <Loading what="album" /> : <>
        <Kpis items={stats} />

        {tagCounts.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 -mb-4">
            {(allTagsOpen ? tagCounts : tagCounts.slice(0, TOP_TAGS_SHOWN)).map(([t, n]) => (
              <TagChip key={t} on={selTags.includes(t)} title={`${n} frames`}
                       onClick={() => setSelTags((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]))}>
                {t} <span className="opacity-60 num">{n}</span>
              </TagChip>
            ))}
            {tagCounts.length > TOP_TAGS_SHOWN && (
              <button type="button" onClick={() => setAllTagsOpen(!allTagsOpen)} className="px-2 text-[12px] font-semibold text-graphite hover:text-forest">
                {allTagsOpen ? 'Fewer' : `+${tagCounts.length - TOP_TAGS_SHOWN} more`}
              </button>
            )}
            {selTags.length > 0 && (
              <button type="button" onClick={() => setSelTags([])} className="px-2 text-[12px] font-semibold text-brick hover:underline">Clear</button>
            )}
          </div>
        )}

        <Section title="Frames" note={`${rows.length} of ${frames.length}`}>
          <table className="w-full min-w-[960px] border-collapse">
            <thead><tr>
              <Th left className="w-14">#</Th><Th left>Frame</Th><Th left>Family</Th><Th>Length</Th>
              <Th>Views</Th><Th>Leads</Th><Th>Likes</Th><Th>Shares</Th><Th>Watch</Th><Th>Load</Th><Th />
            </tr></thead>
            <tbody>
              {rows.length === 0 && <Empty cols={11}>No frames match these filters.</Empty>}
              {rows.map((f) => (
                <tr key={f.code} onClick={() => setOpen(f)} className={`${trClick(false)} group`}>
                  <td className={`${td} text-left`}>
                    {f.position != null
                      ? <span className="font-semibold text-charcoal">{f.position}</span>
                      : <Pill>Bench</Pill>}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-3 min-w-0">
                      <FrameThumb f={f} />
                      <div className="min-w-0 max-w-[380px]">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="flex-none font-mono text-[11.5px] text-graphite">{f.code}</span>
                          <span className="truncate text-[14px] font-semibold text-charcoal">{f.title ?? f.type}</span>
                          {f.cta && <Pill tone="gold">Book-a-visit</Pill>}
                        </div>
                        {f.caption && <div className="mt-0.5 truncate text-[12px] text-graphite">{f.caption}</div>}
                      </div>
                    </div>
                  </td>
                  <td className={`${td} text-left text-graphite text-[13px]`}>{FAMILY_LABELS[familyOf(f.code)]}</td>
                  <td className={`${td} text-right text-graphite`}>{fmtDur(f.duration_s) ?? '—'}</td>
                  <td className={`${td} text-right font-semibold`}>{f.stats.views.toLocaleString('en-IN')}</td>
                  <td className={`${td} text-right`}>{f.stats.unique_leads}</td>
                  <td className={`${td} text-right`}>{f.stats.likes}</td>
                  <td className={`${td} text-right`}>
                    {f.stats.shares}
                    {f.code === 'brochure' && <div className="text-[11px] text-graphite">{f.stats.brochure_opens} opens</div>}
                  </td>
                  <td className={`${td} text-right text-graphite`}>{f.stats.dwell_ms_p50 != null ? fmtMs(f.stats.dwell_ms_p50) : '—'}</td>
                  <td className={`${td} text-right`}><LoadPill f={f} /></td>
                  <td className="pr-3 text-right whitespace-nowrap">
                    <button type="button" onClick={(e) => { e.stopPropagation(); copyLink(f.code); }}
                            className={`${btn} ${btnTone.ghost} h-7 px-2 text-[12px] opacity-0 group-hover:opacity-100 focus:opacity-100`}>
                      {copied === f.code ? 'Copied' : 'Copy link'}
                    </button>
                    <span className="ml-1 text-charcoal/25 group-hover:text-forest transition-colors" aria-hidden>›</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      </>}

      {open && <FrameModal key={open.code} frame={open} onClose={() => setOpen(null)} onSaveTags={saveTags} />}
    </PageFrame>
  );
};

export default Album;
